import { AgentKnowledgeStatus } from "@redotech/crdb-enums/agent-knowledge/agent-knowledge-status";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import { RedoAgentKnowledgeRpcClientContext } from "@redotech/redo-merchant-app-common/agent-knowledge-rpc-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { suggestedMerchantKnowledgeIdFormat } from "@redotech/shopper-agent-knowledge-id";
import { input } from "@redotech/ui/form";
import { plainDateToCurrentDate } from "@redotech/util/temporal";
import { useMutation } from "@tanstack/react-query";
import { memo, ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { KnowledgeCaptureContext } from "../../context/knowledge-capture-context";
import { ACTIVE_CONVERSATION_QUERY_PARAMETER } from "../../query-parameters";

const responseInput = input<string>();

function TitleIcon() {
  return (
    <div
      style={{
        width: "48px",
        height: "48px",
        backgroundColor: "var(--redo-colors-orange-50)",
        borderRadius: "var(--border-radius-full)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "var(--redo-spacing-negative-lg)",
      }}
    >
      <AiStarIcon
        style={{
          width: "24px",
          height: "24px",
          color: "var(--redo-colors-orange-dark-500)",
        }}
      />
    </div>
  );
}

interface ConversationLinkChipProps {
  onClick: () => void;
}

const ConversationLinkChip = memo(function ConversationLinkChip({
  onClick,
}: ConversationLinkChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline",
        background: "none",
        border: "none",
        padding: "var(--redo-spacing-none)",
        margin: "var(--redo-spacing-none)",
        font: "inherit",
        color: "var(--redo-colors-gray-500)",
        textDecoration: "underline",
        cursor: "pointer",
        verticalAlign: "baseline",
      }}
      type="button"
    >
      this recent conversation
      <LinkExternal02Icon
        style={{
          display: "inline",
          width: "12px",
          height: "12px",
          marginLeft: "var(--redo-spacing-xxs)",
          verticalAlign: "middle",
        }}
      />
    </button>
  );
});

function buildSubtitle(
  hasConversation: boolean,
  onViewConversation: () => void,
): ReactNode {
  if (!hasConversation) {
    return "The AI handed off to a human. Add this knowledge, and your AI will handle this scenario going forward.";
  }

  return (
    <>
      In <ConversationLinkChip onClick={onViewConversation} />, the AI didn't
      handle an issue. Add this knowledge, and your AI will handle it correctly
      going forward.
    </>
  );
}

interface KnowledgeCaptureModalProps {
  /** Optional: When provided, overrides context isOpen */
  isOpen?: boolean;
  /** Optional: Callback to close just the modal (keeps toast visible) */
  onCloseModal?: () => void;
}

export const KnowledgeCaptureModal = memo(function KnowledgeCaptureModal({
  isOpen: isOpenProp,
  onCloseModal,
}: KnowledgeCaptureModalProps) {
  const {
    isOpen: isOpenFromContext,
    conversationId,
    suggestions,
    hideKnowledgeCapture,
    editedValues,
    setEditedValues,
    currentIndex,
    setCurrentIndex,
    resolvedSuggestionIndices,
    setResolvedSuggestionIndices,
  } = useRequiredContext(KnowledgeCaptureContext);

  // Use prop if provided, otherwise fall back to context
  const isOpen = isOpenProp ?? isOpenFromContext;
  const agentKnowledgeRpcClient = useRequiredContext(
    RedoAgentKnowledgeRpcClientContext,
  );

  const navigate = useNavigate();
  const location = useLocation();

  const handleViewConversation = useHandler(() => {
    if (!conversationId) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(ACTIVE_CONVERSATION_QUERY_PARAMETER, conversationId);
    // Close modal but keep toast visible so user can reopen
    if (onCloseModal) {
      onCloseModal();
    } else {
      hideKnowledgeCapture();
    }
    navigate(`${location.pathname}?${searchParams}`);
  });

  const allSuggestions = suggestions ?? [];
  const pendingSuggestions = allSuggestions
    .map((suggestion, index) => ({ suggestion, originalIndex: index }))
    .filter(
      ({ originalIndex }) => !resolvedSuggestionIndices.has(originalIndex),
    );

  const totalPending = pendingSuggestions.length;
  const hasMultipleSuggestions = totalPending > 1;

  const safeIndex = Math.min(currentIndex, totalPending - 1);
  const currentEntry = pendingSuggestions[safeIndex >= 0 ? safeIndex : 0];
  const currentSuggestion = currentEntry?.suggestion;
  const currentOriginalIndex = currentEntry?.originalIndex;

  const RESPONSE_LABEL = "AI response";

  const currentEdited =
    currentOriginalIndex !== undefined
      ? editedValues[currentOriginalIndex]
      : undefined;
  const displayResponse =
    currentEdited?.response ?? currentSuggestion?.knowledge ?? "";

  const displayExpiresAt = currentEdited?.expiresAt;

  const responseInputState = useInput(responseInput, displayResponse);
  const [expiresAtState, setExpiresAtState] = useState<
    Temporal.PlainDate | undefined
  >(displayExpiresAt);

  const saveCurrentEditsAndNavigate = useHandler((newIndex: number) => {
    if (currentOriginalIndex === undefined) {
      return;
    }

    const savedEdits = [...editedValues];
    savedEdits[currentOriginalIndex] = {
      response: responseInputState.value,
      expiresAt: expiresAtState,
    };
    setEditedValues(savedEdits);
    setCurrentIndex(newIndex);

    const { suggestion, originalIndex } = pendingSuggestions[newIndex] ?? {};
    if (suggestion) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const edited = savedEdits[originalIndex];
      responseInputState.setValue(edited?.response ?? suggestion.knowledge);
      setExpiresAtState(edited?.expiresAt);
    }
  });

  const handlePrevious = useHandler(() => {
    if (currentIndex > 0) {
      saveCurrentEditsAndNavigate(currentIndex - 1);
    }
  });

  const handleNext = useHandler(() => {
    if (currentIndex < totalPending - 1) {
      saveCurrentEditsAndNavigate(currentIndex + 1);
    }
  });

  const markCurrentAsResolvedAndAdvance = useHandler(
    (newResolvedIndices: Set<number>) => {
      if (newResolvedIndices.size >= allSuggestions.length) {
        hideKnowledgeCapture();
        return;
      }

      const newPending = allSuggestions
        .map((suggestion, index) => ({ suggestion, originalIndex: index }))
        .filter(({ originalIndex }) => !newResolvedIndices.has(originalIndex));

      const newCurrentIndex = Math.min(currentIndex, newPending.length - 1);
      const { suggestion, originalIndex } = newPending[newCurrentIndex] ?? {};

      setResolvedSuggestionIndices(newResolvedIndices);
      setCurrentIndex(newCurrentIndex);

      if (suggestion) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const edited = editedValues[originalIndex];
        responseInputState.setValue(edited?.response ?? suggestion.knowledge);
        setExpiresAtState(edited?.expiresAt);
      }
    },
  );

  const approveKnowledgeMutation = useMutation({
    mutationFn: async (params: {
      knowledgeId: string;
      title: string;
      knowledge: string;
      expiresAt?: Date | null;
      originalIndex: number;
    }) => {
      // Accepting is its own write: it publishes an article and links the
      // proposal to it. The retrieval key is derived, never supplied here.
      await agentKnowledgeRpcClient.acceptKnowledgeSuggestion({
        id: suggestedMerchantKnowledgeIdFormat.schema.parse(params.knowledgeId),
        overrides: { title: params.title, content: params.knowledge },
        availability: {
          status: AgentKnowledgeStatus.Active,
          effectiveStartAt: null,
          effectiveEndAt: params.expiresAt ?? null,
        },
      });
      return { originalIndex: params.originalIndex };
    },
    onSuccess: ({ originalIndex }) => {
      toast("Knowledge added successfully", { variant: "success" });
      const newResolvedIndices = new Set(resolvedSuggestionIndices);
      newResolvedIndices.add(originalIndex);
      markCurrentAsResolvedAndAdvance(newResolvedIndices);
    },
    onError: () => {
      toast("Failed to add knowledge", { variant: "error" });
    },
  });

  const handleAddKnowledge = useHandler(() => {
    if (
      !agentKnowledgeRpcClient ||
      !currentSuggestion ||
      currentOriginalIndex === undefined
    ) {
      return;
    }

    if (!responseInputState.value.trim()) {
      toast(`Please fill in ${RESPONSE_LABEL}`, { variant: "error" });
      return;
    }

    approveKnowledgeMutation.mutate({
      knowledgeId: currentSuggestion.knowledgeId,
      title: currentSuggestion.title,
      knowledge: responseInputState.value,
      expiresAt: expiresAtState ? plainDateToCurrentDate(expiresAtState) : null,
      originalIndex: currentOriginalIndex,
    });
  });

  const handleSkipCurrent = useHandler(() => {
    if (currentOriginalIndex === undefined || !currentSuggestion) {
      return;
    }

    const newResolvedIndices = new Set(resolvedSuggestionIndices);
    newResolvedIndices.add(currentOriginalIndex);
    markCurrentAsResolvedAndAdvance(newResolvedIndices);
  });

  const handleCloseModal = useHandler(() => {
    // Save current edits before closing so they persist when reopened
    if (currentOriginalIndex !== undefined) {
      setEditedValues((prev) => {
        const next = [...prev];
        next[currentOriginalIndex] = {
          response: responseInputState.value,
          expiresAt: expiresAtState,
        };
        return next;
      });
    }

    // Use onCloseModal if provided (keeps toast visible), otherwise dismiss everything
    if (onCloseModal) {
      onCloseModal();
    } else {
      hideKnowledgeCapture();
    }
  });

  const handleSetExpiresAt = useHandler((date: Temporal.PlainDate) => {
    setExpiresAtState(date);
  });

  const handleExpirationModeChange = useHandler(
    (mode: "forever" | "select-date") => {
      if (mode === "forever") {
        setExpiresAtState(undefined);
      } else if (!expiresAtState) {
        // Default to 30 days from now when switching to select-date
        setExpiresAtState(Temporal.Now.plainDateISO().add({ days: 30 }));
      }
    },
  );

  if (!isOpen || !currentSuggestion) {
    return null;
  }

  const hasConversation = !!conversationId;

  const expirationMode = expiresAtState ? "select-date" : "forever";

  const validUntilFooterContent = (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        Knowledge active until
      </Text>
      <div style={{ width: "180px" }}>
        <RedoButtonTabGroup
          disabled={approveKnowledgeMutation.isPending}
          onChange={handleExpirationModeChange}
          options={[
            { value: "forever" as const, el: "Forever" },
            { value: "select-date" as const, el: "Select date" },
          ]}
          size="xs"
          value={expirationMode}
        />
      </div>
      {expiresAtState && (
        <div style={{ width: "180px" }}>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={expiresAtState}
            minDate={Temporal.Now.plainDateISO()}
            setDate={handleSetExpiresAt}
          />
        </div>
      )}
    </Flex>
  );

  return (
    <RedoModal
      additionalFooterContent={validUntilFooterContent}
      footerLayout="column"
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={handleCloseModal}
      primaryButton={{
        text: "Add knowledge",
        onClick: handleAddKnowledge,
        pending: approveKnowledgeMutation.isPending,
        size: "sm",
      }}
      secondaryButton={{ text: "Skip", onClick: handleSkipCurrent, size: "sm" }}
      subtitle={buildSubtitle(hasConversation, handleViewConversation)}
      theme="brand"
      title={currentSuggestion.title}
      TitleIcon={TitleIcon}
    >
      <Flex dir="column" gap="lg" w="full">
        <RedoTextareaInputField
          containerHeight="15vh"
          disabled={approveKnowledgeMutation.isPending}
          fullWidth
          label={RESPONSE_LABEL}
          setValue={responseInputState.setValue}
          value={responseInputState.value}
        />

        {hasMultipleSuggestions && (
          <Flex align="center" gap="sm" justify="center" w="full">
            <RedoButton
              aria-label="Previous"
              disabled={currentIndex === 0}
              hierarchy="tertiary"
              IconLeading={ChevronLeftIcon}
              onClick={handlePrevious}
              size="sm"
            />
            <Text fontSize="sm" textColor="secondary">
              {safeIndex + 1} of {totalPending}
            </Text>
            <RedoButton
              aria-label="Next"
              disabled={currentIndex === totalPending - 1}
              hierarchy="tertiary"
              IconLeading={ChevronRightIcon}
              onClick={handleNext}
              size="sm"
            />
          </Flex>
        )}
      </Flex>
    </RedoModal>
  );
});
